<template>
  <div id="hero-profile-view" class="hero-profile-page">
    <template v-if="profile">
      <section class="hero-intro" aria-labelledby="hero-name">
        <div class="electric-field" aria-hidden="true">
          <span class="electric-line electric-line-one"></span>
          <span class="electric-line electric-line-two"></span>
        </div>
        <v-container class="hero-intro-container">
          <nav class="hero-navigation" aria-label="Hero profiles">
            <router-link
              :to="{ name: 'reckon-way-people', hash: `#${profile.crewSlug}` }"
              class="team-back-link"
            >
              <v-icon small aria-hidden="true">mdi-arrow-left</v-icon>
              Meet the Heroes
            </router-link>

            <div class="hero-navigation-links">
              <router-link
                :to="heroRoute(previousHero)"
                class="hero-navigation-link"
                :aria-label="`Previous hero: ${previousHero.heroName}`"
              >
                <v-icon small aria-hidden="true">mdi-chevron-left</v-icon>
                <span>
                  <small>Previous hero</small>
                  <strong>{{ previousHero.heroName }}</strong>
                </span>
              </router-link>
              <router-link
                :to="heroRoute(nextHero)"
                class="hero-navigation-link hero-navigation-link-next"
                :aria-label="`Next hero: ${nextHero.heroName}`"
              >
                <span>
                  <small>Next hero</small>
                  <strong>{{ nextHero.heroName }}</strong>
                </span>
                <v-icon small aria-hidden="true">mdi-chevron-right</v-icon>
              </router-link>
            </div>
          </nav>

          <div class="hero-intro-grid">
            <div class="hero-intro-copy">
              <p class="hero-overline">Reckon Hero File</p>
              <h1 id="hero-name">{{ profile.heroName }}</h1>
              <p class="hero-title">{{ profile.heroTitle }}</p>
              <blockquote>“{{ profile.heroQuote }}”</blockquote>
              <p class="hero-summary">{{ profile.shortOrigin }}</p>
              <div class="hero-cta-row">
                <a href="#origin" class="hero-cta hero-cta-primary">Read Origin</a>
                <a href="#powers" class="hero-cta hero-cta-secondary">Explore Powers</a>
              </div>
            </div>

            <div class="hero-portrait-shell">
              <div class="portrait-pulse" aria-hidden="true"></div>
              <v-img
                :src="[profile.heroImage, profile.placeholderImage] | mediaSrc"
                :alt="profile.artAlt"
                aspect-ratio="0.78"
                class="hero-portrait"
                contain
              ></v-img>
              <p v-if="!profile.heroImage" class="placeholder-note">
                Temporary hero art — ready to swap in the profile data.
              </p>
            </div>
          </div>

          <dl class="identity-grid" aria-label="Hero identity details">
            <div>
              <dt>Real identity</dt>
              <dd>{{ profile.realName }}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{{ profile.realRole }}</dd>
            </div>
            <div>
              <dt>Power source</dt>
              <dd>{{ profile.powerSource }}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{{ profile.status }}</dd>
            </div>
          </dl>
        </v-container>
      </section>

      <main>
        <section id="origin" class="hero-section origin-section" aria-labelledby="origin-heading">
          <v-container>
            <div class="section-heading">
              <p>Origin</p>
              <h2 id="origin-heading">{{ originHeading }}</h2>
              <span>{{ originDeck }}</span>
            </div>
            <HeroOriginChapters :chapters="profile.originChapters" />
          </v-container>
        </section>

        <section id="powers" class="hero-section powers-section" aria-labelledby="powers-heading">
          <v-container>
            <div class="section-heading">
              <p>Capabilities</p>
              <h2 id="powers-heading">{{ powersHeading }}</h2>
              <span>{{ powersDeck }}</span>
            </div>
            <HeroPowerGrid :powers="profile.powers" />
          </v-container>
        </section>

        <section
          v-if="profile.archNemesis || profile.signatureMove || profile.signatureGear"
          class="hero-section dossier-section"
          aria-label="Hero dossier"
        >
          <v-container class="dossier-grid">
            <article v-if="profile.archNemesis" class="dossier-card nemesis-card">
              <p>{{ profile.archNemesis.label || 'Arch Nemesis' }}</p>
              <h2>{{ profile.archNemesis.name }}</h2>
              <span>{{ profile.archNemesis.description }}</span>
            </article>
            <article v-if="profile.signatureMove" class="dossier-card signature-card">
              <p>
                {{ profile.signatureMove.label || `Signature ${profile.archNemesis ? 'Ability' : 'Move'}` }}
              </p>
              <h2>{{ profile.signatureMove.name }}</h2>
              <span>{{ profile.signatureMove.description }}</span>
              <strong>{{ profile.signatureMove.outcome }}</strong>
            </article>
            <article v-if="profile.signatureGear" class="dossier-card gear-card">
              <p>Signature Gear</p>
              <h2>{{ profile.signatureGear.name }}</h2>
              <span>{{ profile.signatureGear.description }}</span>
            </article>
          </v-container>
        </section>

        <section
          v-if="profile.closingQuote"
          class="hero-quote-section"
          :aria-label="`${profile.heroName} quote`"
        >
          <v-container>
            <blockquote>
              <span>{{ profile.closingQuote }}</span>
              <span v-if="false">
              “Heroes are not born knowing who they will become. They are built through every
              challenge, every choice, and every journey.”
              </span>
            </blockquote>
            <p>— {{ profile.heroName }}</p>
          </v-container>
        </section>

        <section
          v-if="profile.relatedJourneys && profile.relatedJourneys.length"
          class="hero-section journeys-section"
          aria-labelledby="journeys-heading"
        >
          <v-container>
            <div class="section-heading">
              <p>The Reckon Way</p>
              <h2 id="journeys-heading">The Journeys That Built the Hero</h2>
              <span>Real experiences become abilities, stories, and new connections in the Current.</span>
            </div>
            <HeroJourneyGrid :journeys="profile.relatedJourneys" />
          </v-container>
        </section>
      </main>
    </template>

    <v-container v-else class="hero-not-found">
      <h1>Hero File Not Found</h1>
      <p>This alter ego has not entered the Reckon Current yet.</p>
      <v-btn outlined :to="{ name: 'reckon-way-people' }">Return to Meet the Heroes</v-btn>
    </v-container>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import AppFooter from '../components/Footer.vue';
import HeroJourneyGrid from '../components/HeroJourneyGrid.vue';
import HeroOriginChapters from '../components/HeroOriginChapters.vue';
import HeroPowerGrid from '../components/HeroPowerGrid.vue';
import { heroProfileBySlug, heroProfiles } from '../data/heroProfiles';

export default {
  name: 'HeroProfileView',
  components: {
    AppFooter,
    HeroJourneyGrid,
    HeroOriginChapters,
    HeroPowerGrid,
  },
  computed: {
    profile() {
      return heroProfileBySlug[this.$route.params.heroSlug];
    },
    currentHeroIndex() {
      return heroProfiles.findIndex(({ slug }) => slug === this.profile.slug);
    },
    previousHero() {
      const previousIndex = (this.currentHeroIndex - 1 + heroProfiles.length) % heroProfiles.length;
      return heroProfiles[previousIndex];
    },
    nextHero() {
      const nextIndex = (this.currentHeroIndex + 1) % heroProfiles.length;
      return heroProfiles[nextIndex];
    },
    originHeading() {
      return this.profile.originHeading || `${this.profile.heroName}: Origin File`;
    },
    originDeck() {
      return this.profile.originDeck || 'The story behind the hero.';
    },
    powersHeading() {
      return this.profile.powersHeading || `${this.profile.heroName} Super Powers`;
    },
    powersDeck() {
      return this.profile.powersDeck || 'The abilities that shape the mission.';
    },
  },
  methods: {
    heroRoute(hero) {
      return {
        name: this.$route.name === 'super-power-profile' ? 'super-power-profile' : 'hero-profile',
        params: { heroSlug: hero.slug },
      };
    },
  },
};
</script>

<style scoped>
.hero-profile-page {
  background: #05080c;
  color: #eef7fb;
  min-height: 100vh;
}

.hero-intro {
  background:
    radial-gradient(circle at 72% 35%, rgba(66, 187, 240, 0.18), transparent 34%),
    linear-gradient(135deg, #07111a, #05080c 55%, #0a1118);
  border-bottom: 1px solid rgba(151, 225, 255, 0.16);
  overflow: hidden;
  padding: clamp(32px, 6vw, 80px) 0 52px;
  position: relative;
}

.hero-intro-container {
  position: relative;
  z-index: 1;
}

.electric-field,
.electric-line {
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.electric-line::before,
.electric-line::after {
  background: linear-gradient(90deg, transparent, rgba(151, 228, 255, 0.8), transparent);
  content: '';
  height: 1px;
  position: absolute;
  transform: rotate(-20deg);
  width: 42%;
}

.electric-line-one::before {
  right: -5%;
  top: 22%;
}

.electric-line-one::after {
  right: 4%;
  top: 30%;
  width: 22%;
}

.electric-line-two::before {
  bottom: 20%;
  left: -8%;
  transform: rotate(18deg);
}

.electric-line-two::after {
  bottom: 13%;
  left: 8%;
  transform: rotate(26deg);
  width: 18%;
}

.hero-navigation {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 36px;
}

.team-back-link {
  align-items: center;
  color: #9de4ff !important;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 900;
  gap: 7px;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
}

.team-back-link .v-icon {
  color: inherit;
}

.hero-navigation-links {
  display: flex;
  gap: 10px;
}

.hero-navigation-link {
  align-items: center;
  background: rgba(8, 24, 35, 0.82);
  border: 1px solid rgba(141, 223, 255, 0.42);
  border-radius: 7px;
  color: #dff7ff !important;
  display: flex;
  gap: 8px;
  min-width: 160px;
  padding: 9px 12px;
  text-decoration: none;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.hero-navigation-link:hover {
  background: rgba(27, 71, 94, 0.82);
  border-color: #8ddfff;
  transform: translateY(-1px);
}

.hero-navigation-link span {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hero-navigation-link small {
  color: #79bdd7;
  font-size: 0.58rem;
  font-weight: 900;
  letter-spacing: 0.11em;
  line-height: 1.2;
  text-transform: uppercase;
}

.hero-navigation-link strong {
  font-size: 0.78rem;
  line-height: 1.3;
}

.hero-navigation-link-next {
  justify-content: flex-end;
  text-align: right;
}

.hero-navigation-link .v-icon {
  color: #9de4ff;
}

.team-back-link:focus-visible,
.hero-navigation-link:focus-visible,
.hero-cta:focus-visible {
  border-radius: 3px;
  outline: 3px solid #fff;
  outline-offset: 4px;
}

.hero-intro-grid {
  align-items: center;
  display: grid;
  gap: clamp(34px, 7vw, 88px);
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.75fr);
}

.hero-overline,
.section-heading p {
  color: #83dcff;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  margin: 0 0 10px;
  text-transform: uppercase;
}

.hero-intro-copy h1 {
  color: #fff;
  font-size: clamp(3.5rem, 10vw, 7rem);
  letter-spacing: -0.055em;
  line-height: 0.88;
  margin: 0;
  text-shadow: 0 0 38px rgba(82, 196, 245, 0.25);
}

.hero-title {
  color: #a9e8ff;
  font-size: clamp(1.15rem, 3vw, 1.6rem);
  font-weight: 800;
  margin: 18px 0 24px;
}

.hero-intro-copy blockquote {
  border-left: 3px solid #8ee2ff;
  color: #fff;
  font-size: clamp(1.3rem, 3vw, 1.85rem);
  font-weight: 700;
  line-height: 1.45;
  margin: 0 0 24px;
  max-width: 720px;
  padding-left: 20px;
}

.hero-summary {
  color: #bfccd4;
  font-size: 1.02rem;
  line-height: 1.75;
  max-width: 700px;
}

.hero-cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.hero-cta {
  align-items: center;
  border: 1px solid #8ddfff;
  border-radius: 6px;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 900;
  justify-content: center;
  letter-spacing: 0.06em;
  min-height: 46px;
  padding: 10px 18px;
  text-decoration: none;
  text-transform: uppercase;
}

.hero-cta-primary {
  background: #bfefff;
  color: #07111a !important;
}

.hero-cta-secondary {
  color: #bfefff !important;
}

.hero-portrait-shell {
  margin: 0 auto;
  max-width: 430px;
  position: relative;
  width: 100%;
}

.portrait-pulse {
  border: 1px solid rgba(146, 226, 255, 0.52);
  border-radius: 16px;
  inset: -10px;
  opacity: 0.8;
  position: absolute;
  animation: portrait-pulse 3.2s ease-in-out infinite;
}

.hero-portrait {
  background: #09131d;
  border: 1px solid rgba(166, 230, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.52), 0 0 42px rgba(72, 192, 244, 0.16);
  overflow: hidden;
}

.placeholder-note {
  color: #81909a;
  font-size: 0.75rem;
  margin: 12px 0 0;
  text-align: center;
}

.identity-grid {
  border-top: 1px solid rgba(151, 225, 255, 0.14);
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 52px 0 0;
  padding-top: 28px;
}

.identity-grid dt {
  color: #718794;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.identity-grid dd {
  color: #fff;
  font-weight: 800;
  margin: 6px 0 0;
}

.hero-section {
  padding: clamp(68px, 10vw, 120px) 0;
}

.origin-section {
  background: #070b10;
}

.powers-section {
  background: linear-gradient(180deg, #08111a, #05080c);
}

.dossier-section {
  background: #070b10;
  border-top: 1px solid rgba(139, 220, 255, 0.12);
}

.dossier-grid {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.dossier-card {
  background: linear-gradient(145deg, rgba(18, 39, 56, 0.88), rgba(8, 13, 20, 0.98));
  border: 1px solid rgba(141, 220, 255, 0.22);
  border-radius: 10px;
  padding: clamp(28px, 5vw, 52px);
}

.dossier-card:only-child {
  grid-column: 1 / -1;
  margin: 0 auto;
  max-width: 940px;
}

.dossier-card p {
  color: #83dcff;
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.18em;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.dossier-card h2 {
  color: #fff;
  font-size: clamp(2rem, 5vw, 3.4rem);
  letter-spacing: -0.035em;
  line-height: 1;
  margin: 0 0 20px;
}

.dossier-card span,
.dossier-card strong {
  color: #bdcad3;
  display: block;
  line-height: 1.75;
}

.dossier-card strong {
  border-top: 1px solid rgba(141, 220, 255, 0.16);
  color: #e8f7ff;
  margin-top: 22px;
  padding-top: 18px;
}

.nemesis-card {
  border-color: rgba(255, 118, 118, 0.24);
}

.journeys-section {
  background: #070b10;
}

.section-heading {
  margin: 0 auto 50px;
  max-width: 760px;
  text-align: center;
}

.section-heading h2 {
  color: #fff;
  font-size: clamp(2.3rem, 6vw, 4rem);
  letter-spacing: -0.035em;
  line-height: 1;
  margin: 0 0 16px;
}

.section-heading span {
  color: #9dacb6;
  font-size: 1rem;
}

.hero-quote-section {
  background:
    linear-gradient(90deg, rgba(5, 8, 12, 0.92), rgba(17, 46, 65, 0.82), rgba(5, 8, 12, 0.92));
  border-bottom: 1px solid rgba(139, 220, 255, 0.16);
  border-top: 1px solid rgba(139, 220, 255, 0.16);
  padding: clamp(70px, 12vw, 130px) 0;
  text-align: center;
}

.hero-quote-section blockquote {
  color: #fff;
  font-size: clamp(1.8rem, 5vw, 3.6rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.25;
  margin: 0 auto 24px;
  max-width: 1000px;
}

.hero-quote-section p {
  color: #8de0ff;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-not-found {
  min-height: 72vh;
  padding-bottom: 100px;
  padding-top: 100px;
  text-align: center;
}

@keyframes portrait-pulse {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(83, 202, 255, 0.08);
    opacity: 0.35;
  }
  50% {
    box-shadow: 0 0 34px rgba(83, 202, 255, 0.32);
    opacity: 0.9;
  }
}

@media (max-width: 960px) {
  .hero-intro-grid {
    grid-template-columns: 1fr;
  }

  .hero-intro-copy {
    order: 2;
  }

  .hero-portrait-shell {
    max-width: 330px;
    order: 1;
  }

  .identity-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .dossier-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .hero-intro {
    padding-top: 26px;
  }

  .hero-intro-copy h1 {
    font-size: clamp(3.25rem, 18vw, 4.6rem);
  }

  .hero-navigation {
    align-items: stretch;
    flex-direction: column;
    gap: 18px;
  }

  .hero-navigation-links {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-navigation-link {
    min-width: 0;
  }

  .hero-cta-row,
  .hero-cta {
    width: 100%;
  }

  .identity-grid {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  .portrait-pulse {
    animation: none;
    box-shadow: none;
    opacity: 0.55;
  }
}
</style>
